/**
 * Tenants list. Rows open the tenant's detail page — editing, archiving and
 * hard-deleting all happen there, so this page carries no per-row actions.
 * Creating a tenant is still a modal: there is no record to open yet.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    DataTable,
    type ColumnDef,
    Modal,
    Button,
    Badge,
    FormField,
    Input,
    Select,
    formatDateShort,
} from '@shared-ui/components';
import {
    tenantsApi,
    type Tenant,
    type TenantType,
    type CreateTenantRequest,
} from '../../../api/tenantsApi';
import { TENANT_TYPES, TENANT_ID_REGEX, RESERVED_IDS, isReserved, statusVariant } from './tenantHelpers';
import './Tenants.css';

export default function Tenants() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [error, setError] = useState('');

    const { data, isLoading } = useQuery({
        queryKey: ['tenants'],
        queryFn: () => tenantsApi.list(),
    });

    const createMutation = useMutation({
        mutationFn: (body: CreateTenantRequest) => tenantsApi.create(body),
        onSuccess: (created) => {
            queryClient.invalidateQueries({ queryKey: ['tenants'] });
            setShowCreateModal(false);
            setError('');
            // Straight into the new tenant — creating one is normally the first
            // half of setting it up.
            navigate(`/tenants/${created.id}`);
        },
        onError: (err: Error) => setError(err.message),
    });

    const tenants = data?.tenants ?? [];

    const columns: ColumnDef<Tenant>[] = [
        {
            key: 'id',
            header: 'ID',
            render: t => <code className="tenants-id">{t.id}</code>,
        },
        {
            key: 'type',
            header: 'Type',
            render: t => t.type,
        },
        {
            key: 'name',
            header: 'Name',
            render: t => t.name,
        },
        {
            key: 'status',
            header: 'Status',
            render: t => <Badge variant={statusVariant(t.status)}>{t.status}</Badge>,
        },
        {
            key: 'parent',
            header: 'Parent',
            render: t => t.parentTenantId ? <code className="tenants-id">{t.parentTenantId}</code> : '—',
        },
        {
            key: 'createdAt',
            header: 'Created',
            render: t => formatDateShort(t.createdAt),
        },
    ];

    return (
        <div className="tenants-page">
            <header className="tenants-header">
                <h1>Tenants</h1>
                <Button onClick={() => { setShowCreateModal(true); setError(''); }}>
                    + Create tenant
                </Button>
            </header>

            {error && <div className="tenants-error">{error}</div>}

            <DataTable
                columns={columns}
                data={tenants}
                rowKey={t => t.id}
                rowClassName={t => isReserved(t.id) ? 'tenants-row--reserved' : ''}
                onRowClick={t => navigate(`/tenants/${t.id}`)}
                loading={isLoading}
                emptyMessage="No tenants"
                bordered
            />

            <CreateTenantModal
                isOpen={showCreateModal}
                existingIds={tenants.map(t => t.id)}
                onClose={() => { setShowCreateModal(false); setError(''); }}
                onSubmit={body => createMutation.mutate(body)}
                error={error}
                submitting={createMutation.isPending}
            />
        </div>
    );
}

// ── Create modal ───────────────────────────────────────────────────────

function CreateTenantModal(props: {
    isOpen: boolean;
    existingIds: string[];
    onClose: () => void;
    onSubmit: (body: CreateTenantRequest) => void;
    error: string;
    submitting: boolean;
}) {
    const [id, setId] = useState('');
    const [type, setType] = useState<TenantType>('school');
    const [name, setName] = useState('');
    const [parentTenantId, setParentTenantId] = useState('');
    const [localError, setLocalError] = useState('');

    function reset() {
        setId(''); setType('school'); setName(''); setParentTenantId(''); setLocalError('');
    }

    function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setLocalError('');
        if (!TENANT_ID_REGEX.test(id)) {
            setLocalError('id must be lowercase alphanumeric with hyphens (1–32 chars)');
            return;
        }
        if (RESERVED_IDS.has(id)) { setLocalError(`"${id}" is reserved`); return; }
        if (props.existingIds.includes(id)) { setLocalError(`Tenant id "${id}" already exists`); return; }
        props.onSubmit({
            id, type, name: name.trim(),
            ...(parentTenantId ? { parentTenantId } : {}),
        });
    }

    return (
        <Modal
            isOpen={props.isOpen}
            onClose={() => { reset(); props.onClose(); }}
            title="Create tenant"
            size="md"
            footer={
                <>
                    <Button variant="outline" onClick={() => { reset(); props.onClose(); }}>Cancel</Button>
                    <Button onClick={handleSubmit} loading={props.submitting}>Create</Button>
                </>
            }
        >
            <form onSubmit={handleSubmit} className="tenants-form">
                <FormField label="ID (slug)" required>
                    <Input
                        type="text"
                        value={id}
                        onChange={e => setId(e.target.value.toLowerCase())}
                        placeholder="acme-school"
                    />
                    <small>Lowercase alphanumeric + hyphens, 1–32 chars. Cannot be changed later.</small>
                </FormField>

                <FormField label="Type" required>
                    <Select value={type} onChange={e => setType(e.target.value as TenantType)}>
                        {TENANT_TYPES.filter(t => t !== 'public').map(t => (
                            <option key={t} value={t}>{t}</option>
                        ))}
                    </Select>
                </FormField>

                <FormField label="Name" required>
                    <Input type="text" value={name} onChange={e => setName(e.target.value)} />
                </FormField>

                <FormField label="Parent tenant (optional)">
                    <Select value={parentTenantId} onChange={e => setParentTenantId(e.target.value)}>
                        <option value="">— top-level —</option>
                        {props.existingIds.map(p => <option key={p} value={p}>{p}</option>)}
                    </Select>
                </FormField>

                {(localError || props.error) && (
                    <div className="tenants-error">{localError || props.error}</div>
                )}
            </form>
        </Modal>
    );
}
